import { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { LinkButton, useSnackbar } from '@lyearn/ui';
import { useFilterManager } from '@/components/FilterManager';
import useResponse from '@/hooks/useResponse';
import AddOrEditTaskDialog from '@/modules/tasks/components/AddOrEditTaskDialog';
import { useCreateActionItemMutation } from '@/modules/tasks/features/actionItems/graphql/mutations/generated/createActionItem';
import { useCreateActionItemAndUpdateMetricsMutation } from '@/modules/tasks/features/actionItems/graphql/mutations/generated/createActionItemAndUpdateMetrics';
import FileEntity from '@/modules/tasks/file.entity';
import { CreateActionItemInput, RootActionItemFilter } from '@/types/schema';

import useFileMinified from '../../../hooks/useFileMinified';
import { DialogProps } from '../types';

function AddNewTask({
  onClose,
  fileId,
  task: taskToEdit,
  entity,
  entityId,
  defaultAssigneeId,
  participants,
  onRefetchParentProgress,
  actionItemPayload,
  onViewTask,
  inViewFileId,
}: DialogProps) {
  const { file: inViewFile } = useFileMinified(inViewFileId);
  const isSystemGeneratedFile = inViewFile ? FileEntity.isSystemGenerated(inViewFile) : undefined;
  const { t } = useTranslation();
  const { state } = useFilterManager({});
  const viewValue = state.filter?.view;
  const rootActionItemStatus =
    viewValue == 'Separate' ? RootActionItemFilter.Both : RootActionItemFilter.RootActionItems;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, createActionItem] = useCreateActionItemMutation();
  const [, createActionItemAndUpdateMetrics] = useCreateActionItemAndUpdateMetricsMutation();
  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();

  const onCreateActionItem = useCallback(
    async (task: CreateActionItemInput) => {
      setIsSubmitting(true);
      let data: any;
      if (inViewFileId && isSystemGeneratedFile) {
        data = await createActionItemAndUpdateMetrics({
          data: { ...(actionItemPayload ?? {}), ...task },
          filters: {
            id: inViewFileId,
          },
          widgetMetricsFilters: { rootActionItemStatus },
        });
      } else {
        data = await createActionItem({
          data: { ...(actionItemPayload ?? {}), ...task },
        });
      }
      setIsSubmitting(false);
      handleResponse({
        condition: !!data?.data?.createActionItem?.actionItem?.id,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:TaskAdded'),
            action: onViewTask ? (
              <LinkButton
                className="flex-shrink-0 py-2 px-4"
                color={'inverse'}
                onClick={() => {
                  onViewTask(data?.data?.createActionItem?.actionItem?.id!);
                }}>
                {t('Common:Actions.ViewTask')}
              </LinkButton>
            ) : undefined,
          });
          onRefetchParentProgress?.();
          onClose();
        },
      });
    },
    [
      actionItemPayload,
      createActionItem,
      createActionItemAndUpdateMetrics,
      handleResponse,
      inViewFileId,
      isSystemGeneratedFile,
      onClose,
      onRefetchParentProgress,
      onViewTask,
      rootActionItemStatus,
      showSnackbar,
      t,
    ],
  );

  return (
    <AddOrEditTaskDialog
      defaultAssigneeId={defaultAssigneeId}
      entity={entity}
      entityId={entityId}
      fileId={fileId}
      isSubmitting={isSubmitting}
      mode="create"
      participants={participants}
      task={taskToEdit}
      onClose={onClose}
      onCreateActionItem={onCreateActionItem}
    />
  );
}

export default AddNewTask;
